import { BusinessUnit, Centre, Series, VersionedComponentBusinessId } from '@cambridgeassessment/lib-ee-commons';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { IntersectionSpy, useMountEffect } from 'lib-browser';
import {
  getComponentLearners,
  loadComponentDocuments,
  syncSelectAllStatus,
} from '../../../../../store/replacementFiles/thunks';
import ToggleRowLearner from '../ToggleRow/ToggleRowLearner';
import ToggleRowDocument from '../ToggleRow/ToggleRowDocument';
import {
  SummaryContainer,
  SummaryTitle,
  BodyContainer,
  StyledSubtitle,
  StyledExpandableCard,
  StyledLoader,
} from './ComponentCard.styles';
import { ReplacementComponents } from '../../../../../types/ReplacementFiles';
import SelectAllToggle from './SelectAllToggle';

export interface ComponentCardProps {
  componentIndex: number;
  component: ReplacementComponents;
  businessUnitId: BusinessUnit['id'];
  seriesId: Series['id'];
  centreId: Centre['id'];
  learnerNumber: string | null;
  isAllSelected: boolean;
  isAllAbsent?: boolean;
}

const ComponentCard: React.FC<ComponentCardProps> = ({
  componentIndex,
  component,
  businessUnitId,
  seriesId,
  centreId,
  learnerNumber,
  isAllSelected,
  isAllAbsent,
}) => {
  const dispatch = useDispatch();
  const assessmentCode = component.component.assessment.code;
  const componentNumber = component.component.number;
  const componentBid: VersionedComponentBusinessId = {
    componentNumber,
    businessUnitId,
    seriesId,
    centreId,
    assessmentCode,
    assessmentVersion: component.component.assessment.version,
  };

  useMountEffect(() => {
    dispatch(syncSelectAllStatus({ componentBid, componentIndex }));
  });

  const [isLoaded, setIsLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const handleCardExpand = () => {
    setIsExpanded(!isExpanded);

    if (isLoaded) {
      return;
    }

    setIsLoaded(true);
    fetchEntries();

    dispatch(
      loadComponentDocuments(componentIndex, {
        businessUnitId,
        seriesId,
        centreId,
        componentNumber,
        assessmentCode,
      }),
    );
  };

  const { isLoading, nextPageUrl, items: componentLearners } = component.learners;
  const documents = [...component.componentDocuments.items].sort((doc1, doc2) =>
    doc1.section.name.localeCompare(doc2.section.name),
  );

  const fetchEntries = () => {
    if (isLoading) {
      return;
    }

    dispatch(getComponentLearners(componentIndex, componentBid, learnerNumber));
  };

  const hasMultipleLearners = componentLearners.length > 1;

  return (
    <StyledExpandableCard
      summary={
        <SummaryContainer
          data-qa-id={`component-card-summary-${assessmentCode}-${componentNumber}`}
          onClick={handleCardExpand}
        >
          <SummaryTitle>
            {assessmentCode}/{componentNumber} {component.component.name}
          </SummaryTitle>
        </SummaryContainer>
      }
      expanded={isExpanded}
      dataQaId={`component-card-${assessmentCode}-${componentNumber}`}
    >
      <BodyContainer>
        {hasMultipleLearners && (
          <SelectAllToggle
            isAllSelected={isAllSelected}
            isAllAbsent={isAllAbsent}
            componentType={component.component.type}
            componentBid={componentBid}
            componentIndex={componentIndex}
          />
        )}

        {!!documents.length && (
          <>
            <StyledSubtitle data-qa-id={`component-card-${assessmentCode}-${componentNumber}-documents-subtitle`}>
              Allow replacement for these component level sections:
            </StyledSubtitle>
            {documents.map((document) => (
              <ToggleRowDocument key={document.section.id} componentIndex={componentIndex} document={document} />
            ))}
          </>
        )}

        <StyledSubtitle>Candidates</StyledSubtitle>

        {componentLearners.map((entry, entryIndex) => (
          <ToggleRowLearner
            key={entry.learner.number}
            componentIndex={componentIndex}
            entry={entry}
            entryIndex={entryIndex}
            componentType={component.component.type}
          />
        ))}
        {isLoading && <StyledLoader />}
        {!isLoading && nextPageUrl && <IntersectionSpy qaId={'learners-spy'} onIntersection={fetchEntries} />}
      </BodyContainer>
    </StyledExpandableCard>
  );
};

export default React.memo(ComponentCard);
